// ancillary-costs.ts — INCIDENTAL PURCHASE COSTS (book 4.9).
//
// Purchases tied to the job with NO sale counterpart (small equipment, hardware, Amazon orders…).
// MARGIN ONLY, at quote level: the amounts enter the PURCHASE TOTAL and degrade the final margin in €
// and %, but carry ZERO sale price — never on the Sellsy quote, never in any discount base, never in
// the target purchase price or the material summary. No per-line margin.
//
// The supplier is FREE TEXT: the combo suggests workshops + reference distributors, but free entry is
// accepted and stored as is (no reference is created).

import { num, round2 } from "../core/core";

/** One incidental purchase cost entry (quote state, JSONB). `id` = stable key. */
export interface AncillaryCost {
  id: number;
  /** Free-text description (REQUIRED). */
  description?: string;
  /** Supplier — combo label OR free entry, stored as is (REQUIRED). */
  supplier?: string;
  /** Purchase amount excl. VAT (REQUIRED, > 0). */
  amount?: string | number;
}

/** A valid incidental cost entry, with a numeric amount rounded to the cent. */
export interface AncillaryCostCalc {
  id: number;
  description: string;
  supplier: string;
  amount: number;
}

/** Result of the incidental costs block. */
export interface AncillaryCostsResult {
  /** The VALID entries, numeric amounts rounded to the cent. */
  lines: AncillaryCostCalc[];
  /** Σ of valid amounts — THE amount that enters the PURCHASE TOTAL. */
  total: number;
  /** Blocking reasons for INVALID entries (description/supplier/amount) — validation gate. */
  blockers: string[];
}

/**
 * An EMPTY entry (freshly added, nothing typed) is neither valid nor blocking: it is ignored.
 * @param f The entry.
 * @returns True when nothing was typed.
 */
function isEmpty(f: AncillaryCost): boolean {
  return !f.description?.trim() && !f.supplier?.trim() && (f.amount == null || String(f.amount).trim() === "");
}

/**
 * Invalidity reasons for ONE entry (empty = no reason: the entry is simply ignored).
 * @param f The entry.
 * @returns The French reasons the entry is invalid.
 */
export function ancillaryCostReasons(f: AncillaryCost): string[] {
  if (isEmpty(f)) return [];
  const reasons: string[] = [];
  if (!f.description?.trim()) reasons.push("description manquante");
  if (!f.supplier?.trim()) reasons.push("fournisseur manquant");
  if (num(f.amount) <= 0) reasons.push("montant d'achat HT requis (> 0)");
  return reasons;
}

/**
 * Block computation: valid entries + total + validation blockers for the invalid ones. PURE.
 * @param fees The incidental cost entries.
 * @returns The valid lines, their total and the blockers of the invalid entries.
 */
export function computeAncillaryCosts(fees: readonly AncillaryCost[] | undefined): AncillaryCostsResult {
  const lines: AncillaryCostCalc[] = [];
  const blockers: string[] = [];
  let total = 0;
  (fees ?? []).forEach((f, i) => {
    const reasons = ancillaryCostReasons(f);
    if (reasons.length > 0) {
      blockers.push(`Frais d'achat annexe ${i + 1}${f.description?.trim() ? ` (« ${f.description.trim()} »)` : ""} : ${reasons.join(", ")}.`);
      return;
    }
    if (isEmpty(f)) return;
    const amount = round2(num(f.amount));
    lines.push({ id: f.id, description: f.description!.trim(), supplier: f.supplier!.trim(), amount });
    total += amount;
  });
  return { lines, total: round2(total), blockers };
}

/**
 * CLEAN workshop label for the supplier combo: the contact in parentheses is stripped
 * ("KANDA (Quentin)" → "KANDA") — this is what allows deduplication against distributors.
 * @param workshop The workshop label.
 * @returns The label without the trailing contact.
 */
export function cleanWorkshopName(workshop: string): string {
  return workshop.replace(/\s*\([^)]*\)\s*$/, "").trim();
}

/**
 * SUPPLIER combo options: referenced workshops (clean labels) + supply-reference distributors,
 * DEDUPLICATED (case-insensitive — KANDA only once), French alphabetical sort. FREE entry stays
 * accepted by the UI (datalist) — this list is only a helper.
 * @param workshops The referenced workshops.
 * @param distributors The distributors of the supply referential.
 * @returns The sorted, deduplicated supplier labels.
 */
export function ancillaryCostSuppliers(workshops: readonly string[], distributors: readonly string[]): string[] {
  const seen = new Map<string, string>(); // normalised key → label kept (first seen)
  for (const raw of [...workshops.map(cleanWorkshopName), ...distributors]) {
    const label = raw.trim();
    if (!label) continue;
    const key = label.toLocaleLowerCase("fr");
    if (!seen.has(key)) seen.set(key, label);
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b, "fr"));
}
